import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    type SpendCategoryRowDto,
    type SpendInsightsSummaryDto,
    type SpendProductsResponseDto,
    type SpendSupplierRowDto,
    buildSpendInsightsBody,
    getReportSpendLastRequestParams,
    normalizeSpendProductsResponse,
} from 'entities/ReportSpendSummary';
import { $api } from 'shared/api/api';

import {
    type SpendDrillInDimension,
    type SpendDrillInScope,
    buildSpendDrillInRequestParams,
} from '../types/spendDrillIn';

interface UseSpendSummaryDrillInDataParams {
    dimension: SpendDrillInDimension;
    id: string | undefined;
    scope?: SpendDrillInScope;
}

interface UseSpendSummaryDrillInDataResult {
    loading: boolean;
    summary: SpendInsightsSummaryDto | null;
    byCategory: SpendCategoryRowDto[];
    bySupplier: SpendSupplierRowDto[];
    products: SpendProductsResponseDto | null;
    totalSpend: number;
    productsCount: number;
    orderCount: number;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

export const useSpendSummaryDrillInData = ({
    dimension,
    id,
    scope,
}: UseSpendSummaryDrillInDataParams): UseSpendSummaryDrillInDataResult => {
    const lastRequestParams = useSelector(getReportSpendLastRequestParams);

    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<SpendInsightsSummaryDto | null>(
        null,
    );
    const [byCategory, setByCategory] = useState<SpendCategoryRowDto[]>([]);
    const [bySupplier, setBySupplier] = useState<SpendSupplierRowDto[]>([]);
    const [products, setProducts] = useState<SpendProductsResponseDto | null>(
        null,
    );

    const requestBody = useMemo(() => {
        if (!lastRequestParams || !id) {
            return null;
        }

        return buildSpendInsightsBody(
            buildSpendDrillInRequestParams({
                lastRequestParams,
                scope,
                dimension,
                id,
            }),
            {
                orderBy: '-total_spend',
            },
        );
    }, [dimension, id, lastRequestParams, scope]);

    useEffect(() => {
        if (!requestBody) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const [
                    summaryResponse,
                    byCategoryResponse,
                    bySupplierResponse,
                    productsResponse,
                ] = await Promise.all([
                    $api.post<SpendInsightsSummaryDto>(
                        '/v1/reports/spend-insights/summary',
                        requestBody,
                    ),
                    $api.post<{ items: SpendCategoryRowDto[] }>(
                        '/v1/reports/spend-insights/by-category',
                        requestBody,
                    ),
                    $api.post<{ items: SpendSupplierRowDto[] }>(
                        '/v1/reports/spend-insights/by-supplier',
                        requestBody,
                    ),
                    $api.post<SpendProductsResponseDto>(
                        '/v1/reports/spend-insights/products',
                        {
                            ...requestBody,
                            orderBy: '-total_spend',
                            page: 1,
                            pageSize: 25,
                        },
                    ),
                ]);

                if (cancelled) {
                    return;
                }

                setSummary(summaryResponse.data);
                setByCategory(byCategoryResponse.data.items ?? []);
                setBySupplier(bySupplierResponse.data.items ?? []);
                setProducts(
                    normalizeSpendProductsResponse(productsResponse.data),
                );
            } catch {
                if (cancelled) {
                    return;
                }

                setSummary(null);
                setByCategory([]);
                setBySupplier([]);
                setProducts(null);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [requestBody]);

    const totalSpend = useMemo(
        () => Number.parseFloat(summary?.totalSpend ?? '') || 0,
        [summary?.totalSpend],
    );
    const productsCount = products?.count ?? 0;
    const orderCount = summary?.orderCount ?? 0;

    return {
        loading,
        summary,
        byCategory,
        bySupplier,
        products,
        totalSpend,
        productsCount,
        orderCount,
        dateFrom: lastRequestParams?.dateFrom ?? '',
        dateTo: lastRequestParams?.dateTo ?? '',
        datePreset: lastRequestParams?.datePreset,
        lastRequestParamsPresent: Boolean(lastRequestParams),
    };
};
